<template>
    <div id="editproductpage">
        <loader v-if="isLoading" />
        <pre
            class="debug"
            v-if="isDebug"
            >{{ { isQuotation } }}</pre
        >
        <div class="top-grid text-larger">
            <div class="col-a">
                <ui5-button @click="navigationOverview">
                    {{ trans('pages.edit_product.back_to_overview') }}
                </ui5-button>
                <ui5-button @click="$router.push('/input')">
                    {{ trans('pages.edit_product.new_order') }}
                </ui5-button>
                <ui5-button
                    v-if="
                        hasRight('administration') &&
                        productAttachments.length > 0
                    "
                    @click="downloadOrderAttachments"
                >
                    {{
                        trans('pages.edit_product.download_product_attachments')
                    }}
                </ui5-button>


                <div id="btwOfOrder-container">
                    <ui5-label for="btwOfOrder">
                        {{ trans('pages.edit_product.vat') }}
                    </ui5-label>
                    <ui5-select
                        id="btwOfOrder"
                        @change="
                            customerForOrder.btw =
                                $event.detail.selectedOption.value;
                            updateVAT();
                        "
                        v-if="customerForOrder"
                    >
                        <ui5-option
                            v-for="option in customerForOrder.vatOptions"
                            :key="'vatOption.' + option.value"
                            :selected="customerForOrder.btw == option.value"
                            :value="option.value"
                        >
                            {{ option.label }}
                        </ui5-option>
                    </ui5-select>
                </div>

                <div
                    class="paintshop-container"
                    v-if="customerForOrder"
                >
                    <ui5-label for="paintshop">
                        {{ trans('pages.edit_product.paintshop') }}
                    </ui5-label>
                    <ui5-select
                        id="paintshop"
                        @change="
                            customerForOrder.paintshop =
                                $event.detail.selectedOption.value;
                            updatePaintshop();
                        "
                    >
                        <ui5-option :value="null">
                            {{ trans('pages.edit_product.select_paintshop') }}
                        </ui5-option>
                        <ui5-option
                            v-for="option in customerForOrder.paintshopOptions"
                            :key="'paintshopOption.' + option.value"
                            :selected="
                                customerForOrder.paintshop == option.value
                            "
                            :value="option.value"
                        >
                            {{ option.label }}
                        </ui5-option>
                    </ui5-select>
                </div>
            </div>

            <div
                class="col-b quotation-remarks"
                v-if="isQuotation"
            >
                <ui5-label for="quotationRemarks">
                    <span>
                        {{ trans('pages.edit_product.quotation_feedback') }}
                    </span>
                    <spinner v-if="savingRemarks" />
                </ui5-label>
                <ui5-textarea
                    name="quotationRemarks"
                    @input="
                        customerForOrder.quotationRemarks = $event.target.value;
                        saveQuotationRemarksDebounced();
                    "
                    id="quotationRemarks"
                    :value="customerForOrder.quotationRemarks || ''"
                />
            </div>

            <div
                class="col-b"
                v-show="!isQuotation && showPickup"
                id="pickupField"
            >
                <h2>{{ trans('pages.edit_product.pickup') }}</h2>
                <div class="productgrouppagedefaultfields">
                    <div>
                        <b>{{ trans('pages.edit_product.route') }}:</b>
                        <span
                            id="pickupdate"
                            v-if="customerForOrder"
                        >
                            <template
                                v-if="
                                    customerForOrder.pickupDate &&
                                    customerForOrder.pickupDate.id
                                "
                            >
                                {{ customerForOrder.pickupDate.key }} -
                                {{ customerForOrder.pickupDate.description }}
                            </template>
                            <template v-else>-</template>
                        </span>
                    </div>
                    <div>
                        <b>
                            {{ trans('pages.edit_product.delivery_method') }}:
                        </b>
                        <span
                            id="pickupmethod"
                            v-if="customerForOrder"
                        >
                            <template v-if="customerForOrder.pickupMethod">
                                {{ customerForOrder.pickupMethod.name }}
                            </template>
                            <template v-else>-</template>
                        </span>
                    </div>
                </div>
                <ui5-popover ref="tooltipPlanPickup">
                    {{
                        trans(
                            'pages.edit_product.select_paintshop_first_tooltip'
                        )
                    }}
                </ui5-popover>
                <div
                    class="w-fit"
                    ref="containerPlanPickup"
                    @mouseout="$refs.tooltipPlanPickup.close()"
                    @mouseover="
                        !customerForOrder.paintshop &&
                            $refs.tooltipPlanPickup.showAt(
                                $refs.containerPlanPickup
                            )
                    "
                >
                    <ui5-button
                        v-if="customerForOrder"
                        :disabled="!customerForOrder.paintshop"
                        @click="
                            $refs.schedulePickupDatePopup.open(
                                orderId,
                                customerForOrder.pickupDate
                            )
                        "
                    >
                        {{ trans('pages.edit_product.plan_route') }}
                    </ui5-button>
                </div>
            </div>

            <div
                v-if="!isQuotation"
                class="col-c"
                id="deliveryField"
            >
                <h2>{{ trans('pages.edit_product.delivery') }}</h2>
                <div class="productgrouppagedefaultfields">
                    <div>
                        <b>
                            {{
                                trans(
                                    'pages.edit_product.desired_delivery_time'
                                )
                            }}:
                        </b>
                        <span
                            id="preferred-deliverytime"
                            :class="{
                                'shortened-delivery-time':
                                    customerForOrder.deliveryTime != 1,
                            }"
                            v-if="customerForOrder"
                        >
                            {{
                                resolveDeliveryTime(
                                    customerForOrder.deliveryTime
                                )
                            }}
                        </span>
                    </div>
                    <div
                        v-if="
                            customerForOrder &&
                            customerForOrder.deliveryTime == 1
                        "
                    >
                        <b>
                            {{
                                trans(
                                    'pages.edit_product.shown_delivery_weeks'
                                )
                            }}:
                        </b>
                        <span id="preferred-deliveryweeks">
                            {{
                                trans('pages.edit_product.delivery_weeks', {
                                    count: customerForOrder.deliveryWeeks,
                                })
                            }}
                        </span>
                    </div>
                    <div
                        v-if="
                            customerForOrder &&
                            customerForOrder.deliveryTime == 2
                        "
                    >
                        <b>
                            {{
                                trans(
                                    'pages.edit_product.desired_delivery_date'
                                )
                            }}:
                        </b>
                        <span id="preferred-deliverydate">
                            {{ requestedDeliveryDateText }}
                        </span>
                    </div>
                    <div>
                        <b>{{ trans('pages.edit_product.route') }}:</b>
                        <span
                            id="deliverydate"
                            v-if="customerForOrder"
                        >
                            <template
                                v-if="
                                    customerForOrder.deliverydate &&
                                    customerForOrder.deliverydate.id
                                "
                            >
                                {{ customerForOrder.deliverydate.key }} -
                                {{ customerForOrder.deliverydate.description }}
                            </template>
                            <template v-else>-</template>
                        </span>
                    </div>
                    <div>
                        <b>
                            {{
                                trans(
                                    'pages.edit_product.desired_delivery_method'
                                )
                            }}:
                        </b>
                        <span
                            id="preferred-deliverytype"
                            v-if="customerForOrder"
                        >
                            {{
                                resolveDeliveryType(
                                    customerForOrder.requestedDeliveryType
                                )
                            }}
                        </span>
                    </div>
                    <div>
                        <b>
                            {{ trans('pages.edit_product.delivery_method') }}:
                        </b>
                        <span
                            id="deliverymethod"
                            v-if="customerForOrder"
                        >
                            <template v-if="customerForOrder.deliveryMethod">
                                {{ customerForOrder.deliveryMethod.name }}
                            </template>
                            <template>-</template>
                        </span>
                    </div>
                </div>
                <ui5-popover ref="tooltipPlanRoute">
                    {{
                        trans(
                            'pages.edit_product.select_paintshop_first_tooltip'
                        )
                    }}
                </ui5-popover>
                <div
                    class="w-fit"
                    ref="containerPlanRoute"
                    @mouseout="$refs.tooltipPlanRoute.close()"
                    @mouseover="
                        !customerForOrder.paintshop &&
                            $refs.tooltipPlanRoute.showAt(
                                $refs.containerPlanRoute
                            )
                    "
                >
                    <ui5-button
                        v-if="customerForOrder"
                        :disabled="!customerForOrder.paintshop"
                        @click="
                            $refs.schedulePopup.open(
                                orderId,
                                customerForOrder.deliverydate
                            )
                        "
                    >
                        {{ trans('pages.edit_product.plan_route') }}
                    </ui5-button>
                </div>
                <ui5-button
                    :disabled="!customerForOrder.paintshop"
                    v-if="customerForOrder && lastDeliveryDateId"
                    @click="
                        $router.push(
                            '/deliverydatedetails/' + lastDeliveryDateId
                        )
                    "
                >
                    {{ trans('pages.edit_product.to_last_route') }}
                </ui5-button>
            </div>
        </div>

        <div class="columns text-larger">
            <div class="customer-block">
                <p
                    id="customerField"
                    v-if="customerForOrder && customerForOrder.customer"
                >
                    {{ trans('pages.edit_product.active_order') }}:
                    {{ orderId }} - {{ customerForOrder.customer.customerId }} -
                    {{ customerForOrder.customer.customerName }}
                    <template
                        v-if="
                            customerForOrder.customerFields &&
                            customerForOrder.customerFields.Leverrondes
                        "
                    >
                        -
                        {{
                            customerForOrder.customerFields.Leverrondes
                                .selectedOptionLabel
                        }}
                    </template>
                </p>
                <p v-if="customerForOrder && customerForOrder.paymentConditionDescription">
                    <b>{{ trans('pages.edit_product.payment_condition') }}:</b>
                    {{ customerForOrder.paymentConditionDescription }}
                </p>
                <p v-if="customerForOrder && customerForOrder.defaultPaymentTerm !== undefined">
                    <span v-if="customerForOrder.defaultPaymentTerm === 0" class="payment-term-upfront">
                        {{ trans('pages.new_orders.pay_upfront') }}
                    </span>
                    <span v-else-if="customerForOrder.defaultPaymentTerm">
                        <b>{{ trans('pages.edit_product.default_payment_term') }}:</b> {{ trans('pages.edit_product.default_payment_term_days', { days: customerForOrder.defaultPaymentTerm }) }}
                    </span>
                </p>
                <p v-if="customerForOrder">
                    {{ trans('pages.edit_product.production_date') }}:
                    <span>
                        {{ customerForOrder.productionDate || '/' }}
                    </span>
                </p>
            </div>
        </div>

        <div
            id="input-product-edit"
            v-show="showProductEdit"
            class="col-50"
        >
            <span id="product-active">
                <template v-if="productStateParam == 'add-product'">
                    <template v-if="productTypeDetails">
                        {{
                            trans('pages.edit_product.add_new', {
                                product: productTypeDetails.label,
                            })
                        }}
                    </template>
                    <template v-else>
                        {{ trans('pages.edit_product.add_new_product') }}
                    </template>
                </template>
                <template v-else-if="productDetails">
                    {{ productDetails.productNr }}
                    {{ productDetails.producttypeName }}
                </template>
            </span>

            <br />

            <!-- Div containing the default fields -->
            <div
                class="fieldsdiv"
                id="editproductdefaultfields"
                v-if="productDetails"
            >
                <div class="field">
                    <ui5-label
                        for="referenceField"
                        required
                    >
                        {{ trans('pages.edit_product.reference') }}
                    </ui5-label>
                    <ui5-input
                        type="Text"
                        name="referenceField"
                        id="referenceField"
                        :disabled="disableEditFields"
                        :value="productDetails.reference"
                        @input="productDetails.reference = $event.target.value"
                    />
                </div>
                <div class="field">
                    <ui5-label for="nameField">
                        {{ trans('pages.edit_product.place') }}
                    </ui5-label>
                    <ui5-input
                        type="Text"
                        name="nameField"
                        id="nameField"
                        :disabled="disableEditFields"
                        :value="productDetails.name || ''"
                        @input="productDetails.name = $event.target.value"
                    />
                </div>
                <div
                    class="field"
                    v-if="showPrice"
                >
                    <ui5-label for="priceField">
                        {{ trans('pages.edit_product.price') }}
                    </ui5-label>
                    <text-input
                        type="number"
                        step="0.01"
                        name="priceField"
                        id="priceField"
                        :disabled="disableEditFields"
                        v-model="productDetails.price"
                    />
                </div>
                <div
                    class="field"
                    v-if="productDetails.allow_count == 1"
                >
                    <ui5-label for="countField">
                        {{ trans('pages.edit_product.quantity') }}
                    </ui5-label>
                    <text-input
                        type="number"
                        step="1"
                        name="countField"
                        id="countField"
                        :disabled="disableEditFields || true"
                        v-model="productDetails.count"
                    />
                </div>
                <div class="field">
                    <ui5-label for="squareMetersField">
                        {{ trans('pages.edit_product.square_meters') }}
                    </ui5-label>
                    <text-input
                        type="number"
                        name="squareMetersField"
                        id="squareMetersField"
                        :disabled="disableEditFields"
                        v-model="productDetails.squaremeters"
                        readonly
                    />
                </div>
                <template>
                    <div
                        class="field"
                        v-if="productDetails.color"
                    >
                        <ui5-label for="colorField">
                            {{ trans('pages.edit_product.color') }}
                        </ui5-label>
                        <div class="color">
                            <ui5-input
                                type="Text"
                                name="colorField"
                                id="colorField"
                                :disabled="disableEditFields"
                                :value="productDetails.color.id"
                                @input="
                                    productDetails.color.id =
                                        $event.target.value
                                "
                                readonly
                            />
                            <div v-if="colorDescription.length">
                                {{ colorDescription }}
                            </div>
                        </div>
                    </div>
                    <div class="field color-instruction">
                        {{ trans('pages.edit_product.color_instruction') }}
                    </div>
                    <div
                        class="field color-block"
                        v-if="productDetails.color"
                    >
                        <ui5-input
                            @change="searchColor"
                            type="Text"
                            name="ralnrField"
                            id="ralnrField"
                            :disabled="disableEditFields"
                            :value="colorSearch.ralnr"
                            @input="colorSearch.ralnr = $event.target.value"
                            :placeholder="
                                trans('pages.edit_product.ral_number')
                            "
                        />
                        <ui5-input
                            @change="searchColor"
                            type="Text"
                            name="raltypeField"
                            id="raltypeField"
                            :disabled="disableEditFields"
                            :value="colorSearch.raltype"
                            @input="colorSearch.raltype = $event.target.value"
                            :placeholder="trans('pages.edit_product.ral_type')"
                        />
                    </div>
                    <div
                        class="ui5-list-copy"
                        v-if="colorSearch.colorList && productDetails.color"
                    >
                        <div
                            class="ui5-li-copy"
                            v-for="colorOption in colorSearch.colorList"
                            @click="colorSelected(colorOption)"
                            :key="'colorOption.' + colorOption.id"
                        >
                            {{ colorOption.ralnr }}
                            {{ colorOption.raltype }}
                            {{ colorOption.poedernr }}
                            {{ colorOption.producent }} -
                            {{ colorOption.description }}
                        </div>
                    </div>
                </template>
                <div class="field">
                    <ui5-label for="noteField">
                        {{ trans('pages.edit_product.notes') }}
                    </ui5-label>
                    <ui5-textarea
                        id="noteField"
                        :style="{
                            height: height250ProductTypes.includes(
                                productDetails.producttypeId
                            )
                                ? '250px'
                                : 'auto',
                        }"
                        :disabled="disableEditFields"
                        :value="productDetails.note"
                        @input="productDetails.note = $event.target.value"
                    />
                </div>
                <div
                    class="field"
                    v-if="productDetails.allowCanvas"
                >
                    <ui5-label for="canvasId">
                        {{ trans('pages.edit_product.canvas') }}
                    </ui5-label>
                    <ui5-select
                        id="canvasId"
                        :disabled="disableEditFields"
                        @change="
                            productDetails.canvasId =
                                $event.detail.selectedOption.value
                        "
                    >
                        <ui5-option :value="null"></ui5-option>
                        <ui5-option
                            v-for="canvas in productDetails.canvases"
                            :key="'canvas.' + canvas.canvasId"
                            :value="canvas.canvasId"
                            :selected="
                                canvas.canvasId == productDetails.canvasId
                            "
                        >
                            {{
                                canvas.code +
                                (canvas.description
                                    ? ' - ' + canvas.description
                                    : '')
                            }}
                        </ui5-option>
                    </ui5-select>
                </div>
                <div class="field">
                    <ui5-label
                        for="flow"
                        required
                    >
                        {{ trans('pages.edit_product.flow') }}
                    </ui5-label>
                    <ui5-select
                        id="flow"
                        :disabled="disableEditFields"
                        @change="
                            productDetails.flowId =
                                $event.detail.selectedOption.value
                        "
                    >
                        <ui5-option :value="null"></ui5-option>
                        <ui5-option
                            v-for="flow in productDetails.flows"
                            :key="'flow.' + flow.flowId"
                            :value="flow.flowId"
                            :selected="flow.flowId == productDetails.flowId"
                        >
                            {{ flow.label }}
                        </ui5-option>
                    </ui5-select>
                </div>
                <div class="field">
                    <ui5-label for="externalURLField">
                        {{ trans('pages.edit_product.external_url') }}
                    </ui5-label>
                    <ui5-input
                        type="Text"
                        name="externalURLField"
                        id="externalURLField"
                        :disabled="disableEditFields"
                        :value="productDetails.externUrl"
                        @input="productDetails.externUrl = $event.target.value"
                    />
                </div>
                <template
                    v-if="
                        productDetails.isNew &&
                        productTypeDetails.groupWithOtherType == 1
                    "
                >
                    <div class="field">
                        <ui5-label for="linkToProductField">
                            {{ trans('pages.edit_product.selected_product') }}
                        </ui5-label>
                        <ui5-input
                            type="text"
                            name="linkToProductField"
                            id="linkToProductField"
                            :disabled="disableEditFields"
                            :value="productDetails.linkToProductId"
                            @input="
                                productDetails.linkToProductId =
                                    $event.target.value
                            "
                            readonly
                        />
                        <ui5-button
                            @click="productDetails.linkToProductId = ''"
                        >
                            {{ trans('pages.edit_product.clear') }}
                        </ui5-button>
                    </div>
                    <div class="link-product-block">
                        <p>
                            {{ trans('pages.edit_product.product_group_link') }}
                        </p>
                        <ul id="productlistlink">
                            <li
                                v-for="groupItem in productTypeDetails.groupitems"
                                :key="'linkItem.' + groupItem.productId"
                            >
                                <a @click="selectGroupItem(groupItem)">
                                    {{ groupItem.productId }},
                                    {{ groupItem.name }}
                                    {{ groupItem.producttypeName }}
                                </a>
                            </li>
                        </ul>
                    </div>
                </template>
            </div>
            <spinner-text
                class="product-loader"
                v-else-if="productIdParam"
            >
                {{ trans('pages.edit_product.loading_product') }}
            </spinner-text>

            <!-- Div containing the dynamic fields -->
            <div
                class="fieldsdiv"
                id="editproductfields"
                v-if="productDetails && productDetails.datafields"
            >
                <div
                    v-for="field in productDetails.datafields"
                    v-if="field.generalFieldId === null"
                    :class="{
                        field: true,
                        'generic-search-container': isGenericSearch(field),
                        'multiselect-field':
                            field.datatype == dataTypes['multiselect'],
                    }"
                >
                    <template v-if="isDebug">
                        {{
                            {
                                fieldId: field.datafieldId,
                                fieldvalue: field.fieldvalue,
                                valueOption: field.valueOption,
                                name: field.name,
                                isGen: isGenericSearch(field),
                                type: field.datatype,
                            }
                        }}
                    </template>
                    <ui5-label
                        :required="field.mandatory == 1"
                        :style="{
                            position:
                                field.datatype == dataTypes['multiselect']
                                    ? 'absolute'
                                    : '',
                        }"
                        :for="field.datafieldId"
                    >
                        {{
                            field.label || field.name || '#' + field.datafieldId
                        }}
                    </ui5-label>
                    <template v-if="field['datatype'] == dataTypes['int']">
                        <text-input
                            type="number"
                            @change="
                                field.fieldvalue = $event;
                                recalculate(field);
                            "
                            :disabled="disableEditFields"
                            :name="field.datafieldId"
                            :id="field.datafieldId"
                            :value="field.fieldvalue || ''"
                        />
                    </template>
                    <template
                        v-else-if="field['datatype'] == dataTypes['double']"
                    >
                        <text-input
                            type="number"
                            step="0.01"
                            @change="
                                field.fieldvalue = parseFloat($event || 0)
                                  .toFixed(2);
                                recalculate(field);
                            "
                            :disabled="disableEditFields"
                            :name="field.datafieldId"
                            :id="field.datafieldId"
                            :value="field.fieldvalue || ''"
                        />
                    </template>
                    <template
                        v-else-if="field['datatype'] == dataTypes['boolean']"
                    >
                        <ui5-checkbox
                            :name="field.datafieldId"
                            @change="
                                field.fieldvalue = $event.target.checked;
                                recalculate(field);
                            "
                            :disabled="disableEditFields"
                            :id="field.datafieldId"
                            :checked="field.fieldvalue == 1"
                            class="custom"
                        />
                    </template>
                    <template
                        v-else-if="field['datatype'] == dataTypes['options']"
                    >
                        <ui5-select
                            :name="field.datafieldId"
                            @change="
                                field.valueOption =
                                    $event.detail.selectedOption.value;
                                recalculate(field);
                            "
                            :disabled="disableEditFields"
                            :id="field.datafieldId"
                        >
                            <ui5-option
                                v-for="fielddatum in field.fielddata"
                                :key="
                                    'fielddatum.' + fielddatum.datafieldoptionId
                                "
                                :selected="
                                    fielddatum.datafieldoptionId ==
                                    field.valueOption
                                "
                                :value="fielddatum.datafieldoptionId"
                            >
                                {{ fielddatum.dataoption }}
                            </ui5-option>
                        </ui5-select>
                    </template>
                    <template
                        v-else-if="
                            field['datatype'] == dataTypes['multiselect']
                        "
                    >
                        <div
                            v-for="option in field.multiSelectOptions"
                            class="multi-select"
                        >
                            <ui5-checkbox
                                :name="field.datafieldId"
                                :disabled="disableEditFields"
                                @change="
                                    setMultiselectSelected(
                                        field,
                                        option,
                                        $event
                                    );
                                    recalculate(field);
                                "
                                :id="field.datafieldId"
                                :checked="isMultiselectSelected(field, option)"
                                class="custom"
                                :text="option.value"
                            />
                        </div>
                    </template>
                    <template v-else-if="isGenericSearch(field)">
                        <generic-search
                            :field="field"
                            :disabled="disableEditFields"
                            :after-value-change="recalculate(field)"
                        />
                    </template>
                    <template
                        v-else-if="field['datatype'] == dataTypes['textarea']"
                    >
                        <ui5-textarea
                            :name="field.datafieldId"
                            :disabled="disableEditFields"
                            @change="
                                field.fieldvalue = $event.target.value;
                                recalculate(field);
                            "
                            :id="field.datafieldId"
                            :value="field.fieldvalue || ''"
                        />
                    </template>
                    <template v-else>
                        <!-- dataTypes['string'] -->
                        <ui5-input
                            type="Text"
                            @change="
                                field.fieldvalue = $event.target.value;
                                recalculate(field);
                            "
                            :disabled="disableEditFields"
                            :name="field.datafieldId"
                            :id="field.datafieldId"
                            :value="field.fieldvalue || ''"
                        />
                    </template>
                </div>
            </div>

            <ui5-button
                v-if="productDetails"
                :disabled="disableEditFields"
                class="save-button"
                @click="saveProduct"
            >
                <spinner v-if="savingProduct" />
                {{ trans('pages.edit_product.save') }}
            </ui5-button>
        </div>

        <div
            v-show="productStateParam == 'show-producttype'"
            id="input-product-types"
            class="col-50"
        >
            <ui5-list
                v-if="productTypes"
                :header-text="trans('pages.edit_product.add_new_product')"
                :no-data-text="trans('pages.edit_product.no_product_types')"
            >
                <ui5-li
                    v-for="productType in productTypes"
                    @click="
                        producttypeSelected(orderId, productType.producttypeId)
                    "
                    :key="'productType.' + productType.producttypeId"
                >
                    {{ productType.label }}
                </ui5-li>
            </ui5-list>
        </div>

        <div
            id="input-price-line"
            v-show="showInputPriceLine"
            class="col-50"
        >
            <div id="price-line-form-title">{{ formTitle }}</div>
            <template v-if="formItem">
                <div class="field">
                    <ui5-label for="price-line-description-field">
                        {{ trans('pages.edit_product.description') }}
                    </ui5-label>
                    <ui5-input
                        type="Text"
                        :value="formItem.description"
                        @input="formItem.description = $event.target.value"
                        id="price-line-description-field"
                    />
                </div>
                <div class="field">
                    <ui5-label for="price-line-price-field">
                        {{ trans('pages.edit_product.price') }}
                    </ui5-label>
                    <text-input
                        v-model="formItem.price"
                        type="number"
                        id="price-line-price-field"
                        min="0"
                        step=".01"
                    />
                </div>
                <ui5-button
                    id="save-price-line"
                    @click="submitForm"
                >
                    {{ trans('pages.edit_product.save') }}
                </ui5-button>
            </template>
        </div>

        <div class="col-50">
            <ui5-button
                v-if="isQuotation"
                @click="confirmQuotation"
            >
                <span>{{ trans('pages.edit_product.confirm_quotation') }}</span>
                <spinner v-if="confirmingQuotation" />
            </ui5-button>
            <ui5-button
                v-if="isQuotation"
                @click="declineQuotation"
            >
                <span>{{ trans('pages.edit_product.decline_quotation') }}</span>
                <spinner v-if="decliningQuotation" />
            </ui5-button>
            <ui5-popover
                ref="tooltipProcessToProduction"
                v-if="customerForOrder"
            >
                {{
                    !customerForOrder.paintshop
                        ? trans(
                              'pages.edit_product.select_paintshop_first_tooltip'
                          )
                        : trans('pages.edit_product.plan_route_first_tooltip')
                }}
            </ui5-popover>
            <div
                v-if="customerForOrder"
                class="inline-block w-fit"
                ref="containerProcessToProduction"
                @mouseout="$refs.tooltipProcessToProduction.close()"
                @mouseover="
                    !customerForOrder.paintshop ||
                    !(
                        customerForOrder.deliverydate ||
                        customerForOrder.pickupDate
                    )
                        ? $refs.tooltipProcessToProduction.showAt(
                              $refs.containerProcessToProduction
                          )
                        : null
                "
            >
                <ui5-button
                    :disabled="
                        !customerForOrder.paintshop ||
                        !(
                            customerForOrder.deliverydate ||
                            customerForOrder.pickupDate
                        )
                    "
                    v-if="!isQuotation"
                    @click="sendOrderToProduction"
                >
                    {{ trans('pages.edit_product.process_to_production') }}
                </ui5-button>
            </div>
            <ui5-button
                v-if="!isQuotation"
                @click="createCrmOrderConfirmation"
            >
                {{ trans('pages.edit_product.create_order_confirmation') }}
            </ui5-button>
            <ui5-button
                v-if="!isQuotation"
                @click="updateCrmOrderConfirmation"
            >
                {{ trans('pages.edit_product.update_order_confirmation') }}
            </ui5-button>
            <ui5-button
                v-if="!isQuotation"
                @click="moveToNewOrder"
            >
                {{ trans('pages.edit_product.move_to_new_order') }}
            </ui5-button>
            <ui5-button
                v-show="!isQuotation"
                v-bind:disabled="
                    !customerForOrder || !customerForOrder.canBeRejected
                "
                @click="rejectOrder"
            >
                <span>{{ trans('products.reject_order.action') }}</span>
                <spinner v-if="rejectingOrder" />
            </ui5-button>
            <div
                v-if="!isQuotation"
                id="editproduct-wait-for-order-confirmation"
            >
                <ui5-checkbox
                    :checked="waiting"
                    @change="
                        waiting = $event.target.checked;
                        waitForOrderConfirmationChange();
                    "
                    :text="
                        trans('pages.edit_product.wait_for_order_confirmation')
                    "
                />
            </div>

            <br />
            <p id="product-time-container">
                {{ trans('pages.edit_product.production_time') }}:
                <span id="product-total-time">
                    <span class="totalMinutesValue">
                        {{ totalTime.time }}
                        {{ trans('pages.edit_product.minutes') }}
                    </span>
                    <span
                        v-if="totalTime.missing && totalTime.missing.length > 0"
                        class="missing-time"
                    >
                        ({{ trans('pages.edit_product.missing_time_setting') }}
                        {{ totalTime.missing.join(', ') }})
                    </span>
                </span>
            </p>
            <p style="font-size: small">
                {{ trans('pages.edit_product.total') }}
                <span id="product-total-cost">{{ formatPrice(totalCost) }}</span>
            </p>

            <table
                cellspacing="0"
                cellpadding="0"
                id="product-overview"
                class="product-table"
            >
                <thead>
                    <tr class="product-table-header-tr">
                        <th
                            class="product-table-header-td"
                            align="left"
                        >
                            {{ trans('pages.edit_product.select') }}
                        </th>
                        <th
                            class="product-table-header-td"
                            align="left"
                        >
                            {{ trans('pages.edit_product.check') }}
                        </th>
                        <th class="product-table-header-td">
                            {{ trans('pages.edit_product.view') }}
                        </th>
                        <th
                            class="product-table-header-td"
                            align="left"
                        >
                            {{ trans('pages.edit_product.products_in_order') }}
                        </th>
                        <th
                            class="product-table-header-td"
                            align="center"
                        >
                            {{ trans('pages.edit_product.flow') }}
                        </th>
                        <th
                            class="product-table-header-td"
                            align="center"
                        >
                            {{ trans('pages.edit_product.count') }}
                        </th>
                        <th
                            v-if="hasRight('administration')"
                            class="product-table-header-td"
                            align="center"
                        >
                            {{ trans('pages.edit_product.amount') }}
                        </th>
                        <th class="product-table-header-td"></th>
                    </tr>
                </thead>
                <tbody v-if="products">
                    <template v-if="products.length == 0">
                        <tr>
                            <td :colspan="hasRight('administration') ? 7 : 6">
                                <ul>
                                    <li>
                                        {{
                                            trans(
                                                'pages.edit_product.no_products'
                                            )
                                        }}
                                    </li>
                                </ul>
                            </td>
                        </tr>
                    </template>
                    <template v-else>
                        <tr
                            v-for="(product, i) in products"
                            :style="productItemBackgroundColor(product)"
                            :id="'product-row-' + product.productId"
                            class="product-table-item-tr"
                        >
                            <td>
                                <ui5-checkbox
                                    :id="'select-checkbox' + product.productId"
                                    :checked="product.selected"
                                    @change="
                                        product.selected = $event.target.checked
                                    "
                                />
                            </td>
                            <td>
                                <ui5-checkbox
                                    :title="
                                        trans(
                                            'pages.edit_product.product_manually_checked'
                                        )
                                    "
                                    :id="'checkbox' + product.productId"
                                    :checked="product.manualCheck == 1"
                                    @change="toggleManualCheck(product, $event)"
                                />
                            </td>
                            <td
                                @click="
                                    productSelected(product.productId, orderId)
                                "
                                align="center"
                            >
                                <i class="material-icons">search</i>
                            </td>
                            <td>
                                <b>
                                    {{ product.productNr }},
                                    {{ product.producttypeName }}
                                    {{ product.name }} -
                                    {{ product.reference }},
                                    {{ product.productstatus }}
                                </b>
                                <br />
                                <span class="product-note">
                                    {{ product.note }}
                                </span>
                            </td>
                            <td align="center">{{ product.flowName }}</td>
                            <td align="center">{{ product.count }}</td>
                            <td
                                align="center"
                                v-if="hasRight('administration')"
                            >
                                {{ formatPrice(product.price * product.count) }}
                            </td>
                            <td
                                align="center"
                                class="action-column"
                            >
                                <div>
                                    <i
                                        :style="{
                                            visibility:
                                                hasRight('administration') &&
                                                productAttachments.some(
                                                    (x) =>
                                                        x.productId ==
                                                        product.productId
                                                )
                                                    ? 'visible'
                                                    : 'hidden',
                                        }"
                                        class="fas fa-paperclip"
                                        @click="
                                            downloadProductAttachments(product)
                                        "
                                    ></i>
                                    <i
                                        class="material-icons red"
                                        @click="
                                            removeProductConfirmation(product)
                                        "
                                    >
                                        close
                                    </i>
                                </div>
                            </td>
                        </tr>
                    </template>
                </tbody>
            </table>

            <ui5-button @click="addProductClicked">
                {{ trans('pages.edit_product.add_products') }}
            </ui5-button>

            <table
                cellspacing="0"
                cellpadding="0"
                id="price-line-overview"
                class="price-lines-table"
            >
                <thead>
                    <tr class="product-table-header-tr">
                        <th
                            class="product-table-header-td"
                            align="left"
                            id="price-line-select-th"
                        >
                            {{ trans('pages.edit_product.select') }}
                        </th>
                        <th
                            class="product-table-header-td"
                            align="left"
                        >
                            {{ trans('pages.edit_product.check') }}
                        </th>
                        <th class="product-table-header-td">
                            {{ trans('pages.edit_product.view') }}
                        </th>
                        <th
                            class="product-table-header-td"
                            align="left"
                        >
                            {{
                                trans(
                                    'pages.edit_product.price_line_description'
                                )
                            }}
                        </th>
                        <th
                            class="product-table-header-td"
                            align="center"
                        >
                            {{ trans('pages.edit_product.amount') }}
                        </th>
                        <th class="product-table-header-td" />
                    </tr>
                </thead>
                <tbody v-if="priceLines">
                    <tr
                        v-if="priceLines.length == 0"
                        class="product-table-item-tr"
                    >
                        <td
                            colspan="6"
                            class="no-lines"
                        >
                            {{ trans('pages.edit_product.no_price_lines') }}
                        </td>
                    </tr>
                    <template v-else>
                        <tr
                            v-for="priceLine in priceLines"
                            :style="{
                                'background-color':
                                    formItem && priceLine.id == formItem.id
                                        ? 'lightgoldenrodyellow'
                                        : 'burlywood',
                            }"
                            class="product-table-item-tr"
                        >
                            <td class="price-line-selection">
                                <ui5-checkbox
                                    :id="
                                        'price-line-select-checkbox' +
                                        priceLine.productId
                                    "
                                    :checked="priceLine.selected"
                                    @change="
                                        priceLine.selected =
                                            $event.target.checked
                                    "
                                />
                            </td>
                            <td class="price-line-manual-check">
                                <ui5-checkbox
                                    :title="
                                        trans(
                                            'pages.edit_product.price_line_manually_checked'
                                        )
                                    "
                                    @change="
                                        priceLine.manualCheck =
                                            $event.target.checked;
                                        onManualCheck(priceLine);
                                    "
                                    :checked="priceLine.manualCheck"
                                />
                            </td>
                            <td
                                align="center"
                                class="price-line-edit"
                            >
                                <i
                                    class="material-icons"
                                    @click="
                                        showForm(
                                            priceLine,
                                            trans(
                                                'pages.edit_product.edit_price_line'
                                            )
                                        )
                                    "
                                >
                                    search
                                </i>
                            </td>
                            <td class="price-line-description">
                                {{ priceLine.description }}
                            </td>
                            <td
                                align="center"
                                class="price-line-price"
                            >
                                {{ formatPrice(priceLine.price) }}
                            </td>
                            <td
                                align="center"
                                class="price-line-remove"
                            >
                                <i
                                    class="material-icons red"
                                    @click="removePriceLine(priceLine)"
                                >
                                    close
                                </i>
                            </td>
                        </tr>
                    </template>
                </tbody>
            </table>
            <ui5-button
                id="navigation-add-price-line"
                @click="newPriceLine"
            >
                {{ trans('pages.edit_product.add_price_line') }}
            </ui5-button>

            <table
                cellspacing="0"
                cellpadding="0"
                id="profiles-overview"
                class="price-lines-table"
            >
                <thead>
                    <tr class="product-table-header-tr">
                        <th
                            class="product-table-header-td"
                            align="left"
                        >
                            {{ trans('pages.edit_product.product') }}
                        </th>
                        <th
                            class="product-table-header-td"
                            align="left"
                        >
                            {{ trans('pages.edit_product.article_number') }}
                        </th>
                        <th
                            class="product-table-header-td"
                            align="left"
                        >
                            {{ trans('pages.edit_product.description') }}
                        </th>
                        <th
                            class="product-table-header-td"
                            align="left"
                        >
                            {{ trans('pages.edit_product.supplier') }}
                        </th>
                        <th
                            class="product-table-header-td"
                            align="left"
                        >
                            {{
                                trans(
                                    'pages.edit_product.supplier_article_number'
                                )
                            }}
                        </th>
                        <th
                            class="product-table-header-td"
                            align="left"
                        >
                            {{ trans('pages.edit_product.unit') }}
                        </th>
                        <th
                            class="product-table-header-td"
                            align="left"
                        >
                            {{ trans('pages.edit_product.packaging_per_unit') }}
                        </th>
                        <th class="product-table-header-td"></th>
                    </tr>
                </thead>
                <tbody v-if="profiles">
                    <tr
                        class="product-table-item-tr"
                        v-for="(profile, profileIndex) in profiles"
                        :key="'profileIndex.' + profileIndex"
                    >
                        <td>
                            {{ profile.productNr }}
                        </td>
                        <td>
                            {{ profile.profileCode }}
                        </td>
                        <td>
                            {{ profile.profileDescription }}
                        </td>
                        <td>
                            {{ profile.supplierName }}
                        </td>
                        <td>
                            {{ profile.supplierProfileCode }}
                        </td>
                        <td>
                            {{ profile.profileUnitLabel }}
                        </td>
                        <td>
                            {{ profile.profilePackagePerUnit }}
                        </td>
                        <td>
                            <i
                                @click="navigateToOrderLine(profile)"
                                :class="{ 'is-ordered': isOrdered(profile) }"
                                class="fa fa-cart-arrow-down"
                            ></i>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <schedule-delivery-date-popup
            ref="schedulePopup"
            :title="trans('pages.edit_product.schedule_delivery')"
            :on-confirm="onConfirmDeliveryDate"
            :new-delivery-date="newDeliveryDate"
            :workstation-type-ids="workstationTypeIds"
            :deliveryroute="customerDeliveryRoute"
        />
        <schedule-delivery-date-popup
            ref="schedulePickupDatePopup"
            :title="trans('pages.edit_product.schedule_pickup')"
            :on-confirm="onConfirmPickupDate"
            :new-delivery-date="newDeliveryDate"
            :workstation-type-ids="workstationTypeIds"
            :deliveryroute="customerDeliveryRoute"
        />
        <add-order-line-dialog
            ref="addOrderLineDialog"
            :product-id-param="profileToOrder ? profileToOrder.productId : null"
            :profile-id-param="profileToOrder ? profileToOrder.profileId : null"
        ></add-order-line-dialog>
    </div>
</template>
<script>
import productApi from '../api/product';
import productAttachmentApi from '../api/productAttachment';
import IsDebugMixin from '../mixins/is-debug';
import MultipleWatchMixin from '../mixins/multiple-watch-mixin';
import { mapGetters, mapActions, mapMutations } from 'vuex';
import ScheduleDeliveryDatePopup from '../components/deliveryDates/ScheduleDeliveryDatePopup';
import AddOrderLineDialog from '../components/orderLines/AddOrderLineDialog';
import {
    dataTypes,
    getErrorText,
    height250ProductTypes,
    newProductFields,
} from '../shared/editProducts';
import GenericSearch from '../components/products/GenericSearch';
import colorApi from '../api/color';
import customerApi from '../api/customer';
import orderApi from '../api/order';
import priceLineApi from '../api/priceLine';
import Spinner from '../components/loader/Spinner';
import Loader from '../components/loader/Loader';
import moment from 'moment';
import {
    showConfirmDialog,
    showErrorMessage,
    showInfoMessage,
} from '../dialogs';
import SpinnerText from '../components/loader/SpinnerText';
import VueRouter from 'vue-router';
import axios from 'axios';
import { saveAs } from 'file-saver';
import localization from '../mixins/localization';
import TextInput from '../components/TextInput';
import { downloadAttachmentsZip } from '../helpers/FileDownloadHelper';
import CurrencyHelper from '../helpers/CurrencyHelper';

const ORDERSTATE_QUOTATION_VALIDATED = 12;
const ORDERSTATE_QUOTATION_DECLINED = 13;

export default {
    components: {
        SpinnerText,
        Loader,
        Spinner,
        GenericSearch,
        ScheduleDeliveryDatePopup,
        AddOrderLineDialog,
        TextInput,
    },
    mixins: [
        IsDebugMixin,
        MultipleWatchMixin(
            [
                'orderIdParam',
                'productIdParam',
                'productTypeIdParam',
                'productStateParam',
            ],
            'onParamChange'
        ),
        localization,
    ],
    mounted() {
        this.load();
    },
    data() {
        return {
            profileToOrder: null,
            loading: false,
            loadingProduct: false,
            savingProduct: false,
            showProductEdit: true,
            showInputPriceLine: false,
            orderId: null,
            waiting: false,
            customerForOrder: null,
            products: null,
            priceLines: null,
            profiles: null,
            productTypeDetails: null,
            productDetails: null,
            deliverydateDay: '',
            formItem: null,
            formTitle: '',
            colorSearch: {
                ralnr: '',
                raltype: '',
                colorList: null,
            },
            savingRemarks: false,
            confirmingQuotation: false,
            decliningQuotation: false,
            rejectingOrder: false,
            productAttachments: [],
        };
    },
    computed: {
        dataTypes: () => dataTypes,
        saveQuotationRemarksDebounced() {
            return _.debounce(this.saveQuotationRemarks, 1000);
        },
        isLoading() {
            return this.loading || this.loadingProduct;
        },
        isQuotation() {
            if (!this.customerForOrder) {
                return false;
            }
            return this.customerForOrder.isQuotation || false;
        },
        disableEditFields() {
            return this.loadingProduct || this.savingProduct;
        },
        colorDescription() {
            if (!this.productDetails || !this.productDetails.color) {
                return '';
            }
            return (
                (this.productDetails.color.ralnr || '') +
                ' ' +
                (this.productDetails.color.raltype || '') +
                ' ' +
                (this.productDetails.color.poedernr || '') +
                ' ' +
                (this.productDetails.color.producent || '')
            );
        },
        showPrice() {
            return (
                this.hasRight('administration') ||
                this.productDetails.productId == null
            );
        },
        selectedProducts() {
            return (this.products || []).filter((x) => x.selected);
        },
        selectedPriceLines() {
            return (this.priceLines || []).filter((x) => x.selected);
        },
        height250ProductTypes: () => height250ProductTypes,
        totalTime() {
            const result = {
                time: null,
                missing: null,
            };
            if (this.products && this.products.length > 0) {
                let totalMinutes = 0;
                const missingTotalMinutes = new Set();
                this.products.forEach((product) => {
                    if (product.productionMinutes == 0) {
                        missingTotalMinutes.add(product.producttypeName);
                    } else {
                        totalMinutes += product.productionMinutes;
                    }
                });
                result.time = totalMinutes;
                result.missing = Array.from(missingTotalMinutes);
            }

            return result;
        },
        totalCost() {
            const priceLines = this.priceLines || [];
            const products = this.products || [];
            const lines = [...priceLines, ...products];
            return lines.reduce(
                (sum, item) =>
                    sum +
                    parseFloat(item.price) *
                        parseFloat(item.count || item.amount || 1),
                0.0
            );
        },
        requestedDeliveryDateText() {
            const requestedDeliveryDate = moment(
                this.customerForOrder.requestedDeliveryDate
            );
            if (!requestedDeliveryDate.isValid()) {
                return '-';
            }
            return requestedDeliveryDate.format('DD/MM/YYYY');
        },
        showPickup() {
            return (this.products || []).some((x) => x.orderType == 'S');
        },
        orderIdParam() {
            return this.$route.query.orderId;
        },
        productIdParam() {
            return this.$route.query.productId;
        },
        productTypeIdParam() {
            return this.$route.query.productTypeId;
        },
        productStateParam() {
            return this.$route.query.productState;
        },
        ...mapGetters({
            lastDeliveryDateId: 'deliveryDates/getLastClicked',
            hasRight: 'currentUser/hasRight',
            getProductTypeById: 'productTypesCache/getProductTypeById',
            orderedItems: 'editProduct/getOrderedItems',
            isOrdered: 'editProduct/isOrdered',
        }),
        productTypes() {
            return this.$store.getters[
                'productTypesCache/getProductTypesByOrderType'
            ](this.orderIdParam[0] == 'S' ? 'S' : 'O');
        },
        workstationTypeIds() {
            const workstationTypeIds = [];

            if (this.products) {
                this.products.forEach((product) => {
                    if (product.workstationTypeId) {
                        workstationTypeIds.push(product.workstationTypeId);
                    }
                });
            }

            return workstationTypeIds;
        },
        customerDeliveryRoute() {
            if (!this.customerForOrder || !this.customerForOrder.customerFields || !this.customerForOrder.customerFields.Leverrondes) {
                return null;
            }

            return this.customerForOrder.customerFields.Leverrondes.selectedOptionLabel;
        },
    },
    methods: {
        ...mapActions({
            refreshProductTypes: 'productTypesCache/refreshProductTypes',
        }),
        ...mapMutations({
            setOrderedItemsOrderId: 'editProduct/setOrderId',
            addOrderedItem: 'editProduct/addOrderedItem',
        }),
        formatPrice(value) {
            return CurrencyHelper.formatPrice(value, this.currentLocaleIso2Code());
        },
        saveQuotationRemarks() {
            this.savingRemarks = true;
            return orderApi
                .saveQuotationRemarks(
                    this.orderId,
                    this.customerForOrder.quotationRemarks
                )
                .then(() => {
                    this.savingRemarks = false;
                });
        },
        async declineQuotation() {
            if (!this.customerForOrder.quotationRemarks) {
                showInfoMessage(
                    this.trans('pages.edit_product.please_fill_feedback')
                );
                return;
            }

            await this.saveQuotationRemarks();

            this.decliningQuotation = true;
            orderApi
                .setOrderState(this.orderId, ORDERSTATE_QUOTATION_DECLINED)
                .then(() => {
                    this.customerForOrder.orderState =
                        ORDERSTATE_QUOTATION_DECLINED;
                    this.customerForOrder.isQuotation = false;
                    showInfoMessage(
                        this.trans('pages.edit_product.quotation_declined')
                    );
                    this.$router.push('/quotations');
                })
                .catch(() => {
                    showErrorMessage(
                        this.trans(
                            'pages.edit_product.error_declining_quotation'
                        )
                    );
                })
                .finally(() => {
                    this.decliningQuotation = false;
                });
        },
        async confirmQuotation() {
            if (this.customerForOrder.quotationRemarks) {
                await this.saveQuotationRemarks();
            }

            this.confirmingQuotation = true;
            orderApi
                .setOrderState(this.orderId, ORDERSTATE_QUOTATION_VALIDATED)
                .then(() => {
                    this.customerForOrder.orderState =
                        ORDERSTATE_QUOTATION_VALIDATED;
                    this.customerForOrder.isQuotation = false;
                    showInfoMessage(
                        this.trans('pages.edit_product.quotation_confirmed')
                    );
                    this.$router.push('/quotations');
                })
                .catch(() => {
                    showErrorMessage(
                        this.trans(
                            'pages.edit_product.error_confirming_quotation'
                        )
                    );
                })
                .finally(() => {
                    this.confirmingQuotation = false;
                });
        },
        navigateToOrderLine(profile) {
            this.profileToOrder = profile;
            this.$refs.addOrderLineDialog.showPopup();
        },
        onParamChange(changes) {
            if (changes.orderIdParam.changed) {
                this.load();
            } else {
                this.init();
            }
        },
        selectGroupItem(groupItem) {
            if (!this.savingProduct) {
                this.productDetails.linkToProductId = groupItem.productId;
            }
        },
        colorSelected(colorOption) {
            if (!this.savingProduct) {
                this.productDetails.color = { ...colorOption };
                this.colorSearch.colorList = null;
                this.colorSearch.ralnr = '';
                this.colorSearch.raltype = '';
            }
        },
        isGenericSearch(field) {
            return (
                field.datatype == this.dataTypes.multiselect ||
                field.datatype == this.dataTypes.popup
            );
        },
        isMultiselectSelected(field, option) {
            return (field.fieldvalue || '').split('%').includes(option.key);
        },
        setMultiselectSelected(field, option, $event) {
            const checked = $event.target.checked;
            const options = (field.fieldvalue || '').split('%');
            if (checked && !options.includes(option.key)) {
                options.push(option.key);
            } else if (!checked) {
                const optionIndex = options.findIndex((x) => option.key == x);
                if (optionIndex > -1) {
                    options.splice(optionIndex, 1);
                }
            }
            field.fieldvalue = options.join('%');
        },
        //Use this method for any special calculations
        recalculate(field) {
            if (field.name == 'width' || field.name == 'height') {
                const fields = this.productDetails.datafields;
                const widthField = fields.find((x) => x.name == 'width');
                const heightField = fields.find((x) => x.name == 'height');
                if (widthField && heightField) {
                    this.productDetails.squaremeters =
                        (widthField.fieldvalue * heightField.fieldvalue) /
                        1000000;
                }
            }
        },
        searchColor() {
            const ralnr = this.colorSearch.ralnr;
            const raltype = this.colorSearch.raltype;
            if (ralnr.length >= 2 || raltype >= 1) {
                colorApi
                    .searchByRalNrAndType(ralnr, raltype)
                    .then((response) => {
                        this.colorSearch.colorList = response.data.data;
                    });
            }
        },
        toggleManualCheck(product, $event) {
            const checkbox = $event.target;
            const manualCheck = checkbox.checked;
            checkbox.disabled = true;
            productApi
                .updateManualCheck(product.productId, manualCheck)
                .then(() => {
                    product.manualCheck = manualCheck;
                    showInfoMessage(
                        this.trans('pages.edit_product.product_marked')
                    );
                })
                .catch(() => {
                    showErrorMessage(
                        this.trans('pages.edit_product.error_updating')
                    );
                })
                .finally(() => {
                    checkbox.disabled = false;
                });
        },
        productSelected(product, orderId) {
            this.$router
                .push(
                    '/editproduct/?orderId=' +
                        orderId +
                        '&productId=' +
                        product +
                        '&productState=edit-product'
                )
                .catch((ex) => {
                    if (VueRouter.isNavigationFailure(ex)) {
                        this.showInputPriceLine = false;
                        this.showProductEdit = true;
                    }
                });
        },
        onConfirmPickupDate(pickupDate, productionDate, button) {
            button.disabled = true;
            orderApi
                .setPickupDate(this.orderId, pickupDate.id)
                .then(() => {
                    return orderApi.setProductionDate(
                        this.orderId,
                        productionDate
                    );
                })
                .then(() => {
                    showInfoMessage(
                        this.trans('pages.edit_product.pickup_scheduled')
                    );
                    this.$refs.schedulePickupDatePopup.close();
                    this.load();
                })
                .catch(() => {
                    showErrorMessage(
                        this.trans('pages.edit_product.error_scheduling_pickup')
                    );
                })
                .finally(() => {
                    button.disabled = false;
                });
        },
        onConfirmDeliveryDate(deliveryDate, productionDate, button) {
            button.disabled = true;
            orderApi
                .setDeliveryDate(this.orderId, deliveryDate.id)
                .then(() => {
                    return orderApi.setProductionDate(
                        this.orderId,
                        productionDate
                    );
                })
                .then(() => {
                    showInfoMessage(
                        this.trans('pages.edit_product.recalculating_schedule')
                    );
                    this.$refs.schedulePopup.close();
                    this.load();
                })
                .catch(() => {
                    showErrorMessage(
                        this.trans(
                            'pages.edit_product.error_scheduling_delivery'
                        )
                    );
                })
                .finally(() => {
                    button.disabled = false;
                });
        },
        newDeliveryDate() {
            const customer = {
                customerNumber: this.customerForOrder.customer.customerId,
                customerName: this.customerForOrder.customer.customerName,
            };
            this.$router.push(
                '/adddeliverydate?customerForDeliveryDate=' +
                    JSON.stringify(customer)
            );
        },
        producttypeSelected(orderId, productTypeId) {
            this.$router.push(
                '/editproduct/?orderId=' +
                    orderId +
                    '&productTypeId=' +
                    productTypeId +
                    '&productState=add-product'
            );
        },
        createCrmOrderConfirmation($event) {
            $event.target.disabled = true;
            orderApi
                .createCrmOrderConfirmation(this.orderId)
                .then(() => {
                    showInfoMessage(
                        this.trans(
                            'pages.edit_product.order_confirmation_created'
                        )
                    );
                })
                .catch((error) => {
                    const errorText = getErrorText(error);
                    const errorMessage = errorText
                        ? this.trans(
                              'pages.edit_product.error_creating_order_confirmation',
                              { error: errorText }
                          )
                        : this.trans(
                              'pages.edit_product.unknown_error_creating_order_confirmation'
                          );
                    showErrorMessage(errorMessage);
                })
                .finally(() => {
                    $event.target.disabled = false;
                });
        },

        updateVAT() {
            orderApi
                .setVAT(this.orderId, this.customerForOrder.btw)
                .catch(() => {
                    showErrorMessage(
                        this.trans('pages.edit_product.error_updating_vat')
                    );
                });
        },

        updatePaintshop() {
            orderApi
                .setPaintshop(this.orderId, this.customerForOrder.paintshop)
                .catch(() => {
                    showErrorMessage(
                        this.trans(
                            'pages.edit_product.error_updating_paintshop'
                        )
                    );
                });
        },

        waitForOrderConfirmationChange() {
            orderApi
                .updateWaitForOrderConfirmation(this.orderId, this.waiting)
                .then(() => {
                    showInfoMessage(
                        this.trans(
                            'pages.edit_product.wait_for_confirmation_updated'
                        )
                    );
                })
                .catch(() => {
                    showErrorMessage(
                        this.trans(
                            'pages.edit_product.error_updating_wait_for_confirmation'
                        )
                    );
                });
        },

        load() {
            this.loading = true;

            const callInit = () => {
                const promises = [];

                this.$store.commit('general/setOrderId', this.orderId, {
                    root: true,
                });

                promises.push(
                    productAttachmentApi
                        .listByOrderId(this.orderId)
                        .then((response) => {
                            this.productAttachments = response?.data ?? [];
                        })
                        .catch(() => {
                            showErrorMessage(
                                this.trans(
                                    'pages.edit_product.error_loading_attachments'
                                )
                            );
                        })
                );

                promises.push(
                    customerApi
                        .customerForOrder(this.orderId, ['Leverrondes'])
                        .then((response) => {
                            const customerForOrder = response.data.data;
                            this.waiting =
                                customerForOrder.waitForOrderConfirmation;
                            this.customerForOrder = customerForOrder;
                            this.deliverydateDay = (
                                customerForOrder.deliverydate || ''
                            ).id
                                ? (customerForOrder.deliverydate || '').date
                                : '';
                            if (
                                customerForOrder.pickupDate &&
                                customerForOrder.pickupDate.id
                            ) {
                                this.deliverydateDay =
                                    customerForOrder.pickupDate.date;
                            }

                            this.setOrderedItemsOrderId(this.orderId);

                            this.processPopulateTables(response);
                        })
                        .catch(() => {
                            showErrorMessage(
                                this.trans(
                                    'pages.edit_product.error_loading_customer_data'
                                )
                            );
                            this.loading = false;
                        })
                );
                this.init(promises);
            };

            if (this.productIdParam) {
                productApi
                    .fetchOrder(this.productIdParam)
                    .then((response) => {
                        const order = response.data.data;
                        this.orderId = order.orderId;
                        callInit();
                    })
                    .catch(() => {
                        showErrorMessage(
                            this.trans(
                                'pages.edit_product.error_loading_customer_data'
                            )
                        );
                        this.loading = false;
                    });
            } else if (this.orderIdParam) {
                let validationFunc = this.orderIdParam.startsWith('S')
                    ? orderApi.validateServiceOrderExistance
                    : orderApi.validateOrderExistance;
                validationFunc(this.orderIdParam)
                    .then((response) => {
                        this.orderId = response.data.data.orderId;
                        callInit();
                    })
                    .catch(() => {
                        showErrorMessage(
                            this.trans('pages.edit_product.unknown_order_id')
                        );
                        this.loading = false;
                    });
            } else {
                showErrorMessage(this.trans('pages.edit_product.no_order_id'));
            }
        },
        init(promises) {
            this.loading = true;

            promises = promises || [];

            switch (this.productStateParam) {
                case 'add-product':
                    this.showInputPriceLine = false;
                    this.showProductEdit = true;
                    promises.push(
                        this.refreshProductTypes({
                            options: {
                                onBackgroud: false,
                            },
                            params: {
                                forceCheck: true,
                            },
                        }).then(() => {
                            this.loadProductType();
                        })
                    );
                    break;
                case 'show-producttype':
                    this.showInputPriceLine = false;
                    this.showProductEdit = false;
                    promises.push(
                        this.refreshProductTypes({
                            options: {
                                onBackgroud: false,
                            },
                            params: {
                                forceCheck: true,
                            },
                        })
                    );
                    break;
                case 'edit-product':
                default:
                    this.showInputPriceLine = false;
                    this.showProductEdit = true;
                    if (this.productIdParam) {
                        this.loadProduct();
                    }
                    this.$nextTick(() => {
                        this.refreshProductTypes({
                            options: {
                                onBackgroud: false,
                            },
                            params: {
                                forceCheck: true,
                            },
                        });
                    });
                    break;
            }

            Promise.all(promises)
                .catch(() => {
                    showErrorMessage(
                        this.trans('pages.edit_product.error_loading_data')
                    );
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        populateTables() {
            return productApi
                .productsForOrderV2(this.orderId)
                .then((response) => {
                    this.processPopulateTables(response);
                });
        },
        processPopulateTables(response) {
            const orderData = response.data.data;
            let products = [];
            let priceLines = [];
            if (orderData !== undefined) {
                if (orderData.products !== undefined) {
                    products = orderData.products;
                    products.forEach((x) => {
                        x.selected = false;
                    });
                }
                if (orderData.priceLines !== undefined) {
                    priceLines = orderData.priceLines;
                    priceLines.forEach((x) => {
                        x.selected = false;
                    });
                }
            }
            this.products = products;
            this.priceLines = priceLines;
            this.profiles = orderData.profiles;
        },
        productItemBackgroundColor(product) {
            return {
                'background-color':
                    product.productId == this.productIdParam
                        ? 'lightgoldenrodyellow'
                        : 'burlywood',
            };
        },
        loadProduct() {
            this.loadingProduct = true;
            this.productDetails = null;
            return productApi
                .fetchProductDetails(this.productIdParam)
                .then((response) => {
                    const productDetails = response.data.data;
                    if (!productDetails.color) {
                        productDetails.color = { ...newProductFields.color };
                    }
                    productDetails.isNew = false;
                    productDetails.linkToProductId = ''; //Only used in add
                    if (productDetails.price !== null && productDetails.price !== undefined) {
                        productDetails.price = parseFloat(productDetails.price).toFixed(2);
                    }
                    this.productDetails = productDetails;
                    this.$store.commit(
                        'general/setPageTitle',
                        this.trans('pages.edit_product.edit_product_title', {
                            productNr: productDetails.productNr,
                        }),
                        { root: true }
                    );
                })
                .catch((e) => {
                    if (e && axios.isCancel(e)) {
                        return;
                    }
                    showErrorMessage(
                        this.trans('pages.edit_product.error_loading_product')
                    );
                })
                .finally(() => {
                    this.loadingProduct = false;
                });
        },
        loadProductType() {
            const productTypeDetails = this.getProductTypeById(
                this.productTypeIdParam
            );

            this.productTypeDetails = productTypeDetails;

            const productDetails = JSON.parse(JSON.stringify(newProductFields));

            productDetails.producttypeId = this.productTypeIdParam;
            productDetails.allow_count = productTypeDetails.allow_count;
            productDetails.allowCanvas = productTypeDetails.allowCanvas;
            productDetails.flows = productTypeDetails.flows;
            productDetails.isNew = true;
            productDetails.datafields = productTypeDetails.datafields.map(
                (field) => ({ ...field, fieldvalue: field.defaultValue })
            );
            this.productDetails = productDetails;

            this.$store.commit(
                'general/setPageTitle',
                this.trans('pages.edit_product.add_new_product_title', {
                    productType: productTypeDetails.label,
                }),
                { root: true }
            );
        },
        resolveDeliveryTime(deliveryTime) {
            switch (parseInt(deliveryTime)) {
                case 1:
                    return this.trans(
                        'pages.edit_product.delivery_time.standard'
                    );
                case 2:
                    return this.trans(
                        'pages.edit_product.delivery_time.shortened'
                    );
                case 3:
                    return this.trans(
                        'pages.edit_product.delivery_time.express'
                    );
            }
            return '';
        },

        navigationOverview() {
            this.$store.commit('general/setOrderId', '', { root: true });
            this.$router.push('/neworders');
        },

        downloadProductAttachments(product) {
            return this.downloadAttachments(
                () => productAttachmentApi.fetchByProductId(product.productId),
                this.trans('pages.edit_product.attachments')
            );
        },

        downloadOrderAttachments() {
            return this.downloadAttachments(
                () => productAttachmentApi.fetchByOrderId(this.orderId),
                this.trans('pages.edit_product.all_attachments')
            );
        },

        downloadAttachments(fetcherFunction, name) {
            downloadAttachmentsZip(
                fetcherFunction,
                name,
                {
                    downloading: this.trans('pages.edit_product.downloading', {
                        name: name.charAt(0).toUpperCase() + name.slice(1),
                    }),
                    notFound: this.trans('pages.edit_product.no_attachments'),
                    error: this.trans('pages.edit_product.error_downloading', {
                        name: name,
                    })
                }
            );
        },

        resolveDeliveryType(deliveryType) {
            switch (parseInt(deliveryType)) {
                case 1:
                    return this.trans(
                        'pages.edit_product.delivery_type.pickup'
                    );
                case 2:
                    return this.trans(
                        'pages.edit_product.delivery_type.delivery'
                    );
                case 3:
                    return this.trans(
                        'pages.edit_product.delivery_type.assembly'
                    );
            }
            return '';
        },

        saveProduct() {
            switch (this.productStateParam) {
                case 'add-product':
                    this.saveAddProduct();
                    break;
                case 'edit-product':
                    this.saveEditProduct();
                    break;
            }
        },

        updateCrmOrderConfirmation($event) {
            showConfirmDialog({
                title: this.trans(
                    'pages.edit_product.update_order_confirmation_popup.title'
                ),
                content: this.trans(
                    'pages.edit_product.update_order_confirmation_popup.content'
                ),
                confirm: () => {
                    $event.target.disabled = true;
                    orderApi
                        .updateCrmOrderConfirmation(this.orderId)
                        .then(() => {
                            showInfoMessage(
                                this.trans(
                                    'pages.edit_product.order_confirmation_updated'
                                )
                            );
                        })
                        .catch((error) => {
                            const errorText = getErrorText(error);
                            const errorMessage = errorText
                                ? this.trans(
                                      'pages.edit_product.error_updating_order_confirmation',
                                      { error: errorText }
                                  )
                                : this.trans(
                                      'pages.edit_product.unknown_error_updating_order_confirmation'
                                  );
                            showErrorMessage(errorMessage);
                        })
                        .finally(() => {
                            $event.target.disabled = false;
                        });
                },
                closeOnCancel: true,
                closeOnOk: true,
                confirmButton: this.trans(
                    'pages.edit_product.update_order_confirmation_popup.confirm'
                ),
                cancelButton: this.trans(
                    'pages.edit_product.update_order_confirmation_popup.cancel'
                ),
            });
        },
        sendOrderToProduction($event) {
            $event.target.disabled = true;
            productApi
                .productsToProduction(this.orderId)
                .then(() => {
                    showInfoMessage(
                        this.trans(
                            'pages.edit_product.order_sent_to_production'
                        )
                    );
                    this.$router.push('/menu');
                })
                .catch(() => {
                    showErrorMessage(
                        this.trans(
                            'pages.edit_product.error_sending_to_production'
                        )
                    );
                })
                .finally(() => {
                    $event.target.disabled = false;
                });
        },

        moveToNewOrder() {
            const selectedProducts = this.products.filter(
                (product) => product.selected === true
            );
            if (selectedProducts.length === 0) {
                return showInfoMessage(
                    this.trans('pages.edit_product.no_products_selected')
                );
            }

            showConfirmDialog({
                title: this.trans('pages.edit_product.move_products.title'),
                content: this.trans('pages.edit_product.move_products.content'),
                confirm: () => {
                    const productIds = selectedProducts.map(
                        (product) => product.productId
                    );

                    orderApi
                        .moveProductsToNewOrder(this.orderId, productIds)
                        .then((response) => {
                            const newOrderId = response.data.data.orderId;
                            showInfoMessage(
                                this.trans(
                                    'pages.edit_product.new_order_created',
                                    { orderId: newOrderId }
                                )
                            );

                            this.load();
                        })
                        .catch(() => {
                            showErrorMessage(
                                this.trans(
                                    'pages.edit_product.error_moving_products'
                                )
                            );
                        });
                },
                closeOnCancel: true,
                closeOnOk: true,
                confirmButton: this.trans(
                    'pages.edit_product.move_products.confirm'
                ),
                cancelButton: this.trans(
                    'pages.edit_product.move_products.cancel'
                ),
            });
        },

        addProductClicked() {
            let url = '/editproduct/?orderId=' + this.orderId;
            if (this.productIdParam) {
                url += '&productId=' + this.productIdParam;
            }
            if (this.productTypeIdParam) {
                url += '&productTypeId=' + this.productTypeIdParam;
            }
            url += '&productState=show-producttype';
            this.$router.push(url);
        },
        getCommonPayload(received) {
            let price = received.price || 0;
            if (this.showPrice) {
                price = this.productDetails.price || '';
                price = this.productDetails.price.toString().replace(',', '.');
            }

            let hasMandatoryError =
                this.productDetails.reference == '' ||
                this.productDetails.flowId == '';

            const payload = {
                price: price || 0,
                name: this.productDetails.name,
                count: this.productDetails.count,
                note: this.productDetails.note,
                externalUrl: this.productDetails.externUrl,
                reference: this.productDetails.reference,
                color: this.productDetails.color.id,
                canvas: this.productDetails.canvasId,
                flow: this.productDetails.flowId,
                employeeId: this.$store.getters['currentUser/getUserId'],
                linkToProductId: this.productDetails.linkToProductId,
                squareMeters: this.productDetails.squaremeters,
                allowColor: this.productDetails.allowColor,
                allowCanvas: this.productDetails.allowCanvas,
                allowTechnicalFiles: this.productDetails.allowTechnicalFiles,
                attachmentRule: this.productDetails.attachmentRule,
            };

            const relevantFields = this.productDetails.datafields.filter(
                (x) =>
                    received.datafields.find(
                        (y) => x.datafieldId == y.datafieldId
                    ) != null
            );

            relevantFields.forEach((field) => {
                if (field.mandatory == 1 && field.fieldvalue == '') {
                    hasMandatoryError = true;
                }
                const isOptions = field.datatype == dataTypes.options;
                const value = isOptions ? field.valueOption : field.fieldvalue;
                payload[field.datafieldId] = value;
            });

            return { hasMandatoryError, payload };
        },
        saveAddProduct() {
            this.savingProduct = true;
            const received = this.getProductTypeById(this.productTypeIdParam);

            const { hasMandatoryError, payload } =
                this.getCommonPayload(received);

            if (hasMandatoryError) {
                this.savingProduct = false;
                return showErrorMessage(
                    this.trans('pages.edit_product.mandatory_fields_error')
                );
            }

            payload.producttypeId = this.productTypeIdParam;
            payload.orderId = this.orderId;

            productApi
                .addProduct(payload)
                .then(() => {
                    this.$store.commit('general/setOrderId', this.orderId, {
                        root: true,
                    });
                    this.populateTables();
                    showInfoMessage(
                        this.trans('pages.edit_product.product_saved')
                    );
                    this.$router.push(
                        '/editproduct/?orderId=' +
                            this.orderId +
                            '&productState=show-producttype'
                    );
                })
                .catch(() => {
                    showErrorMessage(
                        this.trans('pages.edit_product.error_saving_product')
                    );
                })
                .finally(() => {
                    this.savingProduct = false;
                });
        },

        saveEditProduct() {
            this.savingProduct = true;

            productApi
                .fetchProductDetails(this.productIdParam)
                .then((response) => {
                    const received = response.data.data;

                    const { hasMandatoryError, payload } =
                        this.getCommonPayload(received);

                    if (hasMandatoryError) {
                        this.savingProduct = false;
                        return showErrorMessage(
                            this.trans(
                                'pages.edit_product.mandatory_fields_error'
                            )
                        );
                    }

                    payload.productId = this.productIdParam;

                    productApi
                        .editProduct(payload)
                        .then(() => {
                            this.$store.commit(
                                'general/setOrderId',
                                this.orderId,
                                { root: true }
                            );
                            this.populateTables();
                            showInfoMessage(
                                this.trans('pages.edit_product.product_saved')
                            );
                            this.$router.push(
                                '/editproduct/?orderId=' +
                                    this.orderId +
                                    '&productState=show-producttype'
                            );
                        })
                        .catch(() => {
                            showErrorMessage(
                                this.trans(
                                    'pages.edit_product.error_saving_product'
                                )
                            );
                        })
                        .finally(() => {
                            this.savingProduct = false;
                        });
                })
                .catch(() => {
                    showErrorMessage(
                        this.trans(
                            'pages.edit_product.error_fetching_product_type'
                        )
                    );
                    this.savingProduct = false;
                });
        },

        removeProductConfirmation(product) {
            const orderId = this.orderId;
            showConfirmDialog({
                title: this.trans('pages.edit_product.remove_product.title'),
                content: this.trans(
                    'pages.edit_product.remove_product.content',
                    { productNr: product.productNr }
                ),
                confirm: (dialog) => {
                    dialog.close();
                    productApi
                        .remove(product.productId)
                        .then(() => {
                            this.load();
                        })
                        .catch(() => {
                            showErrorMessage(
                                this.trans(
                                    'pages.edit_product.product_cannot_be_removed'
                                )
                            );
                        });
                },
                cancel: (dialog) => {
                    dialog.close();
                },
                confirmButton: this.trans(
                    'pages.edit_product.remove_product.confirm'
                ),
                cancelButton: this.trans(
                    'pages.edit_product.remove_product.cancel'
                ),
            });
        },

        showForm(item, title) {
            this.formTitle = title;
            this.formItem = { ...item };
            this.showProductEdit = false;
            this.showInputPriceLine = true;
        },

        onManualCheck(priceLine) {
            priceLineApi
                .manualCheck(priceLine.id, priceLine.manualCheck)
                .then(() => {
                    showInfoMessage(
                        this.trans('pages.edit_product.price_line_marked')
                    );
                })
                .catch(() => {
                    priceLine.manualCheck = !priceLine.manualCheck;
                    showErrorMessage(
                        this.trans('pages.edit_product.unknown_error')
                    );
                });
        },

        newPriceLine() {
            this.showForm(
                { id: null, price: 0, description: '' },
                this.trans('pages.edit_product.add_price_line')
            );
        },

        removePriceLine(priceLine) {
            showConfirmDialog({
                title: this.trans('pages.edit_product.remove_price_line.title'),
                content: this.trans(
                    'pages.edit_product.remove_price_line.content'
                ),
                confirm: (dialog) => {
                    dialog.close();
                    priceLineApi
                        .remove(priceLine.id)
                        .then(() => {
                            this.populateTables();
                            showInfoMessage(
                                this.trans(
                                    'pages.edit_product.price_line_removed'
                                )
                            );
                            this.showInputPriceLine = false;
                        })
                        .catch(() => {
                            showErrorMessage(
                                this.trans('pages.edit_product.unknown_error')
                            );
                        });
                },
                cancel: (dialog) => {
                    dialog.close();
                },
                confirmButton: this.trans(
                    'pages.edit_product.remove_price_line.confirm'
                ),
                cancelButton: this.trans(
                    'pages.edit_product.remove_price_line.cancel'
                ),
            });
        },

        submitForm() {
            const payload = {
                id: this.formItem.id,
                description: this.formItem.description,
                price: this.formItem.price,
                orderId: this.orderId,
            };

            const func = priceLineApi[payload.id ? 'update' : 'add'];
            func(payload)
                .then(() => {
                    this.newPriceLine();
                    this.populateTables();
                    showInfoMessage(
                        this.trans('pages.edit_product.price_line_saved')
                    );
                })
                .catch((error) => {
                    showErrorMessage(
                        this.trans('pages.edit_product.unknown_error')
                    );
                });
        },

        rejectOrder($event) {
            showConfirmDialog({
                title: this.trans('products.reject_order.confirmation.title'),
                content: this.trans(
                    'products.reject_order.confirmation.content'
                ),
                confirm: () => {
                    $event.target.disabled = true;
                    orderApi
                        .rejectOrder(
                            this.orderId,
                            this.$store.getters['currentUser/getUserId']
                        )
                        .then(() => {
                            showInfoMessage(
                                this.trans('products.reject_order.success')
                            );

                            this.navigationOverview();
                        })
                        .catch((error) => {
                            showErrorMessage(
                                this.trans('products.reject_order.error')
                            );
                        })
                        .finally(() => {
                            $event.target.disabled = false;
                        });
                },
                closeOnCancel: true,
                closeOnOk: true,
                confirmButton: this.trans(
                    'products.reject_order.confirmation.actions.confirm'
                ),
                cancelButton: this.trans(
                    'products.reject_order.confirmation.actions.cancel'
                ),
            });
        },
    },
};
</script>
